// Story 048 — Read-only banner for reader windows.
//
// Two display modes:
//   1. "writer-held"   — another user holds the lock; show their name.
//   2. "phase-frozen"  — DA is in a frozen state (LOCKED, REJECTED,
//                       EVALUATION_DONE, NOT_STARTED); banner reads
//                       "Data acquisition is in {state} — read-only".
//
// Spec: docs/stories/shipped/lyse/048-single-writer-multi-reader.md §"banner".
// The exact copy is regex-asserted in the acceptance spec; do not
// silently rephrase.
//
// SB6-ANA-HEADER-01 — banner compacted from the default <Alert> chrome
// (~56 px) to a single-row info bar (~36 px). Christian: "pick a size
// you think fits". Implemented by overriding the Alert root padding +
// keeping the AlertTitle on a single line. Copy stays verbatim so the
// 048 acceptance regexes still match.
import { Alert, AlertTitle } from "@mui/material";
import { useLockStore } from "./lockStore.js";
import { useLockStatusQuery } from "./queries.js";
import { LockIcon } from "@aeriosiq/ui/icons";

interface ReadOnlyCollaboratorBannerProps {
  dataAcquisitionId: string;
  /** True when the calling user is the writer-eligible holder (D-5). */
  isWriter: boolean;
  /** Current evaluation state — drives the phase-frozen copy.
   *  SB3-CC-G (lyse/v1@6.0.0): single-axis 8-value vocabulary. */
  evaluationState:
    | "CREATED"
    | "PROCESSING"
    | "PRE_SCREENING"
    | "EXPERT_ANALYSIS"
    | "EVALUATION_DONE"
    | "LOCKED"
    | "ISSUE"
    | "REJECTED"
    | null;
}

function relativeTimeFromNow(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "just now";
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 60) return "just now";
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${String(min)} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function ReadOnlyCollaboratorBanner({
  dataAcquisitionId,
  isWriter,
  evaluationState,
}: ReadOnlyCollaboratorBannerProps): React.ReactElement | null {
  // Always subscribe to the store for consistent reactivity. The polled
  // status query is what surfaces the holder name when we're a reader.
  const lockState = useLockStore((s) => s.state);
  const lockStatus = useLockStatusQuery({
    dataAcquisitionId,
    enabled: !isWriter,
  });

  // Writer holding the lock — no banner.
  if (isWriter && lockState.kind === "holding") return null;

  // Phase-frozen states get the universal copy. We render the banner
  // only when the lock store says we're idle/contended/free — when the
  // analyst is the writer we never show this UI even on a frozen phase
  // (the editor surface is gated by editPermission downstream).
  const phaseFrozenLabels: Record<string, string> = {
    CREATED: "a not-yet-started phase",
    PROCESSING: "an ML-processing phase",
    EVALUATION_DONE: "a finalised review phase",
    LOCKED: "a signed-off phase",
    ISSUE: "a flagged-issue phase",
    REJECTED: "a rejected phase",
  };
  const frozenLabel =
    evaluationState != null ? phaseFrozenLabels[evaluationState] : undefined;
  if (frozenLabel) {
    return (
      <Alert
        severity="info"
        icon={<LockIcon fontSize="small" />}
        role="status"
        aria-live="polite"
        tabIndex={0}
        data-testid="analysis-lock-banner-phase"
        // SB6-ANA-HEADER-01 — compact chrome (~36 px). py 0.25 collapses
        // the vertical padding; the .MuiAlert-message slot's default
        // py:8 is overridden too so the row matches the icon height.
        sx={{
          py: 0.25,
          alignItems: "center",
          "& .MuiAlert-message": { py: 0 },
          "& .MuiAlert-icon": { py: 0, mr: 1 },
          "&:focus-visible": {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <AlertTitle sx={{ mb: 0, fontWeight: 500 }}>
          Read-only — this data acquisition is in {frozenLabel}.
        </AlertTitle>
      </Alert>
    );
  }

  // Reader sees writer holder via the contention store state or the
  // polled status query.
  let holderName: string | null = null;
  let heldSince: string | null = null;
  if (lockState.kind === "contended") {
    holderName = lockState.heldBy.displayName;
    heldSince = lockState.heldSince;
  } else if (lockStatus.data?.status === "held") {
    holderName = lockStatus.data.holder.displayName;
    heldSince = lockStatus.data.heldSince;
  }

  if (!holderName) return null;

  const sinceLabel = heldSince ? relativeTimeFromNow(heldSince) : "just now";

  return (
    <Alert
      severity="info"
      icon={<LockIcon fontSize="small" />}
      role="status"
      aria-live="polite"
      tabIndex={0}
      // SB6-ANA-HEADER-01 — compact chrome (~36 px). Same overrides as
      // the phase-frozen branch above.
      sx={{
        py: 0.25,
        alignItems: "center",
        "& .MuiAlert-message": { py: 0 },
        "& .MuiAlert-icon": { py: 0, mr: 1 },
        "&:focus-visible": {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <AlertTitle sx={{ mb: 0, fontWeight: 500 }}>
        Read-only — {holderName} is editing (last edit {sinceLabel})
      </AlertTitle>
    </Alert>
  );
}
